import { FocusEvent } from "react";
import { Autocomplete, TextField } from "@mui/material";
import useAddressAutocomplete from "@/hooks/use-autocomplete";

interface BillingAddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  error?: boolean;
  helperText?: string | false;
}

// Google Places-backed Billing Address field. Reuses the same autocomplete hook
// as the Service Address field, but is intentionally "suggestions only":
// `freeSolo` lets the admin type any value (PO boxes, non-standard or
// intentionally different billing addresses) — we do NOT force a prediction
// match, because billing address is optional and need not be a mappable place.
const BillingAddressAutocomplete = ({
  value,
  onChange,
  onBlur,
  error,
  helperText,
}: BillingAddressAutocompleteProps) => {
  const { getPlacePredictions, placePredictions } = useAddressAutocomplete();

  return (
    <Autocomplete
      freeSolo
      filterOptions={(x) => x}
      inputValue={value ?? ""}
      options={placePredictions.map((prediction) => ({
        label: prediction.description,
        value: prediction,
      }))}
      onInputChange={(_evt, newValue, reason) => {
        // "input" = user typing; "clear" = the built-in clear (X) button, which
        // sends "". Both sync the form + refresh predictions. "reset" fires when
        // a prediction is selected and is handled by onChange below.
        if (reason === "input" || reason === "clear") {
          onChange(newValue);
          getPlacePredictions({ input: newValue.trim() });
        }
      }}
      onChange={(_evt, selected) => {
        // Prediction selected (object) or free-text committed (string);
        // `null` = cleared via the clear button.
        if (selected == null) {
          onChange("");
        } else if (typeof selected === "string") {
          onChange(selected);
        } else {
          onChange(String(selected.label));
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          id="billingAddress"
          data-testid="billingAddress"
          error={error}
          helperText={helperText}
          onBlur={onBlur}
          fullWidth
        />
      )}
    />
  );
};

export default BillingAddressAutocomplete;
